import axiosInstance from '../../../axiosInstance'
import apiEndpoints from '../../../api'

// ---------------------------------------------------------------------------
// Employee Shifts — /api/shifts and /api/shift-assignments.
//
// Society context rides on the global X-Society-Id header that axiosInstance
// sets, so nothing here passes society_id explicitly.
//
// Assigning a shift to one employee usually happens on the employee endpoints
// instead (shift_id / shift_effective_from / weekly_off_days on POST and PUT
// /api/employees) — those live in EmployeeReqs.
// ---------------------------------------------------------------------------

// Query params: an absent filter must not be sent at all, so undefined / null /
// '' are all dropped.
const cleanParams = (obj) => {
    const params = {};
    Object.entries(obj).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') params[key] = value;
    });
    return params;
};

// Request bodies: null is MEANINGFUL here (full_day_minutes: null = inherit the
// society default, effective_to: null = open-ended) so only undefined and ''
// are dropped. PATCH bodies are built from the same helper, which is why a
// field the form never touched must arrive as undefined, not null.
const cleanBody = (obj) => {
    const body = {};
    Object.entries(obj).forEach(([key, value]) => {
        if (value !== undefined && value !== '') body[key] = value;
    });
    return body;
};

const SHIFT_FIELDS = [
    'shift_code', 'shift_name', 'description', 'start_time', 'end_time',
    'break_minutes', 'break_type', 'full_day_minutes', 'half_day_minutes',
    'late_grace_minutes', 'early_exit_grace_minutes', 'overtime_after_minutes',
    'punch_in_window_before_minutes', 'punch_out_window_after_minutes',
    'color_hex', 'display_order', 'is_default', 'is_active',
];

const ASSIGNMENT_FIELDS = [
    'employee_id', 'shift_id', 'effective_from', 'effective_to',
    'weekly_off_days', 'notes', 'is_active',
];

// An assignment belongs to the person it was created for, so employee_id is
// not patchable — dropping it here means a form that keeps it in state can
// never leak it into the request.
const ASSIGNMENT_PATCH_FIELDS = ASSIGNMENT_FIELDS.filter(f => f !== 'employee_id');

// Callbacks and path ids travel in the same action payload as the body fields —
// picking an explicit whitelist keeps onSuccess/shift_id out of the request.
const pick = (source, fields) => {
    const out = {};
    fields.forEach((f) => { if (f in source) out[f] = source[f]; });
    return cleanBody(out);
};

// ---- Shift definitions — /api/shifts --------------------------------------

// GET /api/shifts/enums — no permission. Returns break_types and the labelled
// weekday list, so the 0-is-Sunday numbering is never hardcoded.
export const getShiftEnumsReq = () =>
    axiosInstance.get(apiEndpoints.SOCIETY_SHIFT_ENUMS_API);

// GET /api/shifts — paginated, ordered by display_order then start_time.
export const getShiftsReq = (action) => {
    const d = action?.data || {};
    return axiosInstance.get(apiEndpoints.SOCIETY_SHIFTS_API, {
        params: cleanParams({
            is_active: d.is_active,
            search: d.search,
            page: d.page || 1,
            limit: d.limit || 20,
        }),
    });
};

// POST /api/shifts — shift.manage. shift_code is uppercased server-side.
export const createShiftReq = (action) =>
    axiosInstance.post(apiEndpoints.SOCIETY_SHIFTS_API, pick(action?.data || {}, SHIFT_FIELDS));

export const getShiftByIdReq = (action) => {
    const url = apiEndpoints.SOCIETY_SHIFT_BY_ID_API.replace(':id', action?.data?.shift_id);
    return axiosInstance.get(url);
};

// PATCH /api/shifts/:id — any subset of the create fields, at least one.
// Omitted fields keep their stored value; there are no defaults on update.
export const updateShiftReq = (action) => {
    const d = action?.data || {};
    const url = apiEndpoints.SOCIETY_SHIFT_BY_ID_API.replace(':id', d.shift_id);
    return axiosInstance.patch(url, pick(d, SHIFT_FIELDS));
};

// DELETE /api/shifts/:id — 409 SHIFT_IN_USE, carrying the count of assignments
// still pointing at it. The screen offers "deactivate instead".
export const deleteShiftReq = (action) => {
    const url = apiEndpoints.SOCIETY_SHIFT_BY_ID_API.replace(':id', action?.data?.shift_id);
    return axiosInstance.delete(url);
};

// ---- Assignments — /api/shift-assignments ---------------------------------

// GET /api/shift-assignments — newest effective_from first. `active_on` finds
// the assignments covering one day INCLUDING open-ended ones, which a plain
// effective_to filter would miss.
export const getShiftAssignmentsReq = (action) => {
    const d = action?.data || {};
    return axiosInstance.get(apiEndpoints.SOCIETY_SHIFT_ASSIGNMENTS_API, {
        params: cleanParams({
            employee_id: d.employee_id,
            shift_id: d.shift_id,
            is_active: d.is_active,
            active_on: d.active_on,
            page: d.page || 1,
            limit: d.limit || 20,
        }),
    });
};

// POST /api/shift-assignments — shift.assign. Unlike the employee endpoints
// this does NOT auto-close an existing assignment: an overlap is a 409. Use it
// when the dates need explicit control.
export const createShiftAssignmentReq = (action) =>
    axiosInstance.post(apiEndpoints.SOCIETY_SHIFT_ASSIGNMENTS_API, pick(action?.data || {}, ASSIGNMENT_FIELDS));

export const getShiftAssignmentByIdReq = (action) => {
    const url = apiEndpoints.SOCIETY_SHIFT_ASSIGNMENT_BY_ID_API.replace(':id', action?.data?.assignment_id);
    return axiosInstance.get(url);
};

// PATCH /api/shift-assignments/:id — for CORRECTIONS: a mistyped start date,
// a changed weekly off. To move somebody to a different shift, end this one
// and create the next; editing shift_id in place makes the past claim they
// always worked the new shift.
export const updateShiftAssignmentReq = (action) => {
    const d = action?.data || {};
    const url = apiEndpoints.SOCIETY_SHIFT_ASSIGNMENT_BY_ID_API.replace(':id', d.assignment_id);
    return axiosInstance.patch(url, pick(d, ASSIGNMENT_PATCH_FIELDS));
};

// POST /api/shift-assignments/:id/end — close an open assignment on a date.
// Returns the updated assignment. Half of the manual "move to another shift":
// end this one, then create the next from the following day.
export const endShiftAssignmentReq = (action) => {
    const d = action?.data || {};
    const url = apiEndpoints.SOCIETY_END_SHIFT_ASSIGNMENT_API.replace(':id', d.assignment_id);
    return axiosInstance.post(url, { effective_to: d.effective_to });
};

// DELETE /api/shift-assignments/:id — hard delete, only for an assignment
// created by mistake. For a shift genuinely worked, end it instead so the
// period stays on record.
export const deleteShiftAssignmentReq = (action) => {
    const url = apiEndpoints.SOCIETY_SHIFT_ASSIGNMENT_BY_ID_API.replace(':id', action?.data?.assignment_id);
    return axiosInstance.delete(url);
};
